// src/utils/BookingLockManager.js
// Optimistic locking system for concurrent booking operations

import {
    collection,
    doc,
    getDocs,
    limit,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    updateDoc,
    where
} from 'firebase/firestore';
import { ErrorTrackingService } from '../services/ErrorTrackingService';
import { FirebaseService } from '../services/FirebaseService';

export class BookingLockManager {
  
  // Configuration
  static DEFAULT_LOCK_TIMEOUT = parseInt(process.env.REACT_APP_BOOKING_LOCK_TIMEOUT) || 30000; // 30 seconds
  static MAX_LOCKS_PER_BUSINESS = 100; // Prevent lock spam
  static CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
  static LOCK_COLLECTION = 'booking_locks';
  
  // Lock status types
  static LOCK_STATUS = {
    ACTIVE: 'active',
    RELEASED: 'released',
    EXPIRED: 'expired',
    FAILED: 'failed'
  };

  // ==========================================
  // CORE LOCK OPERATIONS
  // ==========================================

  /**
   * Acquire a booking lock for a specific time slot
   */
  static async acquireLock(businessId, timeSlot, options = {}) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const lockData = this.createLockData(businessId, timeSlot, options);
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock acquisition started',
        'lock_manager',
        'info',
        { 
          businessId, 
          lockId: lockData.lockId,
          timeSlot: timeSlot.start,
          duration: timeSlot.duration
        }
      );
      
      // Check for existing active locks on this time slot
      const existingLock = await this.checkExistingLocks(businessId, timeSlot);
      if (existingLock.hasActiveLock) {
        ErrorTrackingService.addBreadcrumb(
          'Lock acquisition failed - existing active lock',
          'lock_manager',
          'warning',
          { 
            businessId, 
            existingLockId: existingLock.lockId,
            requestedLockId: lockData.lockId
          }
        );
        
        return {
          success: false,
          error: 'Time slot is currently locked by another operation',
          existingLock: existingLock.lockInfo,
          lockId: null
        };
      }
      
      // Acquire lock atomically
      const result = await runTransaction(db, async (transaction) => {
        // Double-check for locks within transaction
        const recheck = await this.checkExistingLocks(businessId, timeSlot);
        if (recheck.hasActiveLock) {
          throw new Error('Time slot became locked during transaction');
        }
        
        // Create lock document
        const lockRef = doc(collection(db, this.LOCK_COLLECTION));
        transaction.set(lockRef, {
          ...lockData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        return {
          lockRef: lockRef.id,
          lockId: lockData.lockId
        };
      });
      
      // Schedule automatic cleanup
      this.scheduleAutoCleanup(result.lockRef, lockData.timeout);
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock acquired successfully',
        'lock_manager',
        'info',
        { 
          businessId, 
          lockId: lockData.lockId,
          lockRef: result.lockRef,
          timeout: lockData.timeout
        }
      );
      
      return {
        success: true,
        lockId: lockData.lockId,
        lockRef: result.lockRef,
        timeSlot: lockData.timeSlot,
        expiresAt: lockData.expiresAt,
        acquiredAt: new Date().toISOString(),
        timeout: lockData.timeout
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'acquireLock',
        businessId,
        timeSlot
      });
      
      return {
        success: false,
        error: error.message,
        lockId: null
      };
    }
  }

  /**
   * Release a booking lock
   */
  static async releaseLock(lockInfo) {
    try {
      const { db } = FirebaseService.getInstances();
      
      if (!lockInfo || !lockInfo.lockRef) {
        throw new Error('Invalid lock information provided');
      }
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock release started',
        'lock_manager',
        'info',
        { 
          lockId: lockInfo.lockId,
          lockRef: lockInfo.lockRef
        }
      );
      
      // Update lock status atomically
      await runTransaction(db, async (transaction) => {
        const lockRef = doc(db, this.LOCK_COLLECTION, lockInfo.lockRef);
        
        transaction.update(lockRef, {
          status: this.LOCK_STATUS.RELEASED,
          releasedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock released successfully',
        'lock_manager',
        'info',
        { 
          lockId: lockInfo.lockId,
          lockRef: lockInfo.lockRef
        }
      );
      
      return {
        success: true,
        lockId: lockInfo.lockId,
        releasedAt: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'releaseLock',
        lockId: lockInfo?.lockId,
        lockRef: lockInfo?.lockRef
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Force release a lock (admin function)
   */
  static async forceReleaseLock(lockRef, reason = 'Force release') {
    try {
      const { db } = FirebaseService.getInstances();
      
      await runTransaction(db, async (transaction) => {
        const lockDocRef = doc(db, this.LOCK_COLLECTION, lockRef);
        
        transaction.update(lockDocRef, {
          status: this.LOCK_STATUS.RELEASED,
          releasedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          forceReleased: true,
          forceReleaseReason: reason
        });
      });
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock force released',
        'lock_manager',
        'warning',
        { lockRef, reason }
      );
      
      return { success: true, lockRef, reason };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'forceReleaseLock',
        lockRef,
        reason
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // LOCK VALIDATION AND CHECKING
  // ==========================================

  /**
   * Check for existing locks on a time slot
   */
  static async checkExistingLocks(businessId, timeSlot) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const now = new Date().toISOString();
      
      // Query for active locks that overlap with the time slot
      const locksQuery = query(
        collection(db, this.LOCK_COLLECTION),
        where('businessId', '==', businessId),
        where('status', '==', this.LOCK_STATUS.ACTIVE),
        where('expiresAt', '>', now)
      );
      
      const locksSnapshot = await getDocs(locksQuery);
      
      let hasActiveLock = false;
      let lockInfo = null;
      
      // Check each active lock for time slot overlap
      locksSnapshot.docs.forEach(lockDoc => {
        const lockData = lockDoc.data();
        
        if (this.checkTimeSlotOverlap(timeSlot, lockData.timeSlot)) {
          hasActiveLock = true;
          lockInfo = {
            lockId: lockData.lockId,
            lockRef: lockDoc.id,
            timeSlot: lockData.timeSlot,
            acquiredAt: lockData.acquiredAt,
            expiresAt: lockData.expiresAt,
            source: lockData.source
          };
        }
      });
      
      return {
        hasActiveLock,
        lockInfo,
        totalActiveLocks: locksSnapshot.docs.length
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'checkExistingLocks',
        businessId
      });
      
      return {
        hasActiveLock: false,
        lockInfo: null,
        error: error.message
      };
    }
  }

  /**
   * Validate if a lock is still active and valid
   */
  static async validateLock(lockInfo) {
    try {
      const { db } = FirebaseService.getInstances();
      
      if (!lockInfo || !lockInfo.lockRef) {
        return { isValid: false, reason: 'Invalid lock info' };
      }
      
      const lockRef = doc(db, this.LOCK_COLLECTION, lockInfo.lockRef);
      const lockDoc = await import('firebase/firestore').then(m => m.getDoc(lockRef));
      
      if (!lockDoc.exists()) {
        return { isValid: false, reason: 'Lock document not found' };
      }
      
      const lockData = lockDoc.data();
      const now = new Date();
      const expiresAt = new Date(lockData.expiresAt);
      
      // Check if lock has expired
      if (now > expiresAt) {
        return { isValid: false, reason: 'Lock has expired' };
      }
      
      // Check if lock status is still active
      if (lockData.status !== this.LOCK_STATUS.ACTIVE) {
        return { isValid: false, reason: `Lock status is ${lockData.status}` };
      }
      
      // Check if lock ID matches
      if (lockData.lockId !== lockInfo.lockId) {
        return { isValid: false, reason: 'Lock ID mismatch' };
      }
      
      return {
        isValid: true,
        lockData,
        remainingTime: expiresAt.getTime() - now.getTime()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'validateLock',
        lockId: lockInfo?.lockId
      });
      
      return {
        isValid: false,
        reason: 'Validation failed due to error',
        error: error.message
      };
    }
  }

  /**
   * Extend lock timeout (if needed for long operations)
   */
  static async extendLock(lockInfo, additionalTime = 30000) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const validation = await this.validateLock(lockInfo);
      if (!validation.isValid) {
        return { success: false, error: validation.reason };
      }
      
      const newExpiresAt = new Date(Date.now() + additionalTime).toISOString();
      
      await runTransaction(db, async (transaction) => {
        const lockRef = doc(db, this.LOCK_COLLECTION, lockInfo.lockRef);
        
        transaction.update(lockRef, {
          expiresAt: newExpiresAt,
          updatedAt: serverTimestamp(),
          extended: true,
          extensionTime: additionalTime
        });
      });
      
      ErrorTrackingService.addBreadcrumb(
        'Booking lock extended',
        'lock_manager',
        'info',
        { 
          lockId: lockInfo.lockId,
          additionalTime,
          newExpiresAt
        }
      );
      
      return {
        success: true,
        newExpiresAt,
        additionalTime
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'extendLock',
        lockId: lockInfo?.lockId,
        additionalTime
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // CLEANUP AND MAINTENANCE
  // ==========================================

  /**
   * Clean up expired locks
   */
  static async cleanupExpiredLocks(businessId = null) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const now = new Date().toISOString();
      let expiredQuery = query(
        collection(db, this.LOCK_COLLECTION),
        where('status', '==', this.LOCK_STATUS.ACTIVE),
        where('expiresAt', '<', now),
        limit(50) // Process in batches
      );
      
      // Add business filter if provided
      if (businessId) {
        expiredQuery = query(
          collection(db, this.LOCK_COLLECTION),
          where('businessId', '==', businessId),
          where('status', '==', this.LOCK_STATUS.ACTIVE),
          where('expiresAt', '<', now),
          limit(50)
        );
      }
      
      const expiredSnapshot = await getDocs(expiredQuery);
      let cleanedCount = 0;
      
      // Process expired locks in batches
      for (const lockDoc of expiredSnapshot.docs) {
        try {
          await runTransaction(db, async (transaction) => {
            transaction.update(lockDoc.ref, {
              status: this.LOCK_STATUS.EXPIRED,
              expiredAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          });
          
          cleanedCount++;
        } catch (error) {
          console.warn('Failed to expire lock:', lockDoc.id, error);
        }
      }
      
      ErrorTrackingService.addBreadcrumb(
        'Expired locks cleanup completed',
        'lock_manager',
        'info',
        { 
          businessId: businessId || 'all',
          cleanedCount,
          foundExpired: expiredSnapshot.docs.length
        }
      );
      
      return {
        success: true,
        cleanedCount,
        foundExpired: expiredSnapshot.docs.length
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'cleanupExpiredLocks',
        businessId
      });
      
      return {
        success: false,
        error: error.message,
        cleanedCount: 0
      };
    }
  }

  /**
   * Get lock statistics for monitoring
   */
  static async getLockStatistics(businessId) {
    try {
      const { db } = FirebaseService.getInstances();
      
      // Query all locks for the business
      const allLocksQuery = query(
        collection(db, this.LOCK_COLLECTION),
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      
      const allLocksSnapshot = await getDocs(allLocksQuery);
      
      const stats = {
        total: allLocksSnapshot.docs.length,
        active: 0,
        released: 0,
        expired: 0,
        failed: 0,
        averageLockTime: 0,
        oldestActiveLock: null,
        newestLock: null
      };
      
      const lockTimes = [];
      const now = new Date();
      
      allLocksSnapshot.docs.forEach(lockDoc => {
        const lockData = lockDoc.data();
        
        // Count by status
        switch (lockData.status) {
          case this.LOCK_STATUS.ACTIVE:
            stats.active++;
            if (!stats.oldestActiveLock || new Date(lockData.acquiredAt) < new Date(stats.oldestActiveLock)) {
              stats.oldestActiveLock = lockData.acquiredAt;
            }
            break;
          case this.LOCK_STATUS.RELEASED:
            stats.released++;
            break;
          case this.LOCK_STATUS.EXPIRED:
            stats.expired++;
            break;
          case this.LOCK_STATUS.FAILED:
            stats.failed++;
            break;
        }
        
        // Calculate lock duration for released/expired locks
        if (lockData.releasedAt || lockData.expiredAt) {
          const endTime = new Date(lockData.releasedAt || lockData.expiredAt);
          const startTime = new Date(lockData.acquiredAt);
          const duration = endTime.getTime() - startTime.getTime();
          lockTimes.push(duration);
        }
        
        // Track newest lock
        if (!stats.newestLock || new Date(lockData.createdAt) > new Date(stats.newestLock)) {
          stats.newestLock = lockData.createdAt;
        }
      });
      
      // Calculate average lock time
      if (lockTimes.length > 0) {
        stats.averageLockTime = Math.round(lockTimes.reduce((sum, time) => sum + time, 0) / lockTimes.length);
      }
      
      return {
        success: true,
        stats,
        businessId,
        generatedAt: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'getLockStatistics',
        businessId
      });
      
      return {
        success: false,
        error: error.message,
        stats: null
      };
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Create lock data structure
   */
  static createLockData(businessId, timeSlot, options = {}) {
    const lockId = `lock_${businessId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timeout = options.timeout || this.DEFAULT_LOCK_TIMEOUT;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + timeout);
    
    return {
      lockId,
      businessId,
      timeSlot: {
        start: timeSlot.start,
        end: timeSlot.end,
        duration: timeSlot.duration || 60
      },
      status: this.LOCK_STATUS.ACTIVE,
      acquiredAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      timeout,
      source: options.source || 'booking_creation',
      metadata: options.metadata || {}
    };
  }

  /**
   * Check if two time slots overlap
   */
  static checkTimeSlotOverlap(slot1, slot2) {
    try {
      const start1 = new Date(slot1.start);
      const end1 = new Date(slot1.end || new Date(start1.getTime() + (slot1.duration || 60) * 60000));
      
      const start2 = new Date(slot2.start);
      const end2 = new Date(slot2.end || new Date(start2.getTime() + (slot2.duration || 60) * 60000));
      
      return start1 < end2 && start2 < end1;
      
    } catch (error) {
      return false;
    }
  }

  /**
   * Schedule automatic lock cleanup
   */
  static scheduleAutoCleanup(lockRef, timeout) {
    // Schedule cleanup slightly after timeout to ensure lock has expired
    setTimeout(async () => {
      try {
        const { db } = FirebaseService.getInstances();
        const lockDocRef = doc(db, this.LOCK_COLLECTION, lockRef);
        
        // Check if lock is still active and expired
        const lockDoc = await import('firebase/firestore').then(m => m.getDoc(lockDocRef));
        
        if (lockDoc.exists()) {
          const lockData = lockDoc.data();
          const now = new Date();
          const expiresAt = new Date(lockData.expiresAt);
          
          if (lockData.status === this.LOCK_STATUS.ACTIVE && now > expiresAt) {
            await updateDoc(lockDocRef, {
              status: this.LOCK_STATUS.EXPIRED,
              expiredAt: serverTimestamp(),
              autoExpired: true
            });
          }
        }
      } catch (error) {
        console.warn('Auto cleanup failed for lock:', lockRef, error);
      }
    }, timeout + 5000); // Add 5 seconds buffer
  }

  /**
   * Emergency cleanup - remove all locks for a business (admin function)
   */
  static async emergencyCleanup(businessId, reason = 'Emergency cleanup') {
    try {
      const { db } = FirebaseService.getInstances();
      
      const businessLocksQuery = query(
        collection(db, this.LOCK_COLLECTION),
        where('businessId', '==', businessId),
        where('status', '==', this.LOCK_STATUS.ACTIVE)
      );
      
      const locksSnapshot = await getDocs(businessLocksQuery);
      let cleanedCount = 0;
      
      for (const lockDoc of locksSnapshot.docs) {
        try {
          await updateDoc(lockDoc.ref, {
            status: this.LOCK_STATUS.RELEASED,
            releasedAt: serverTimestamp(),
            emergencyCleanup: true,
            emergencyReason: reason
          });
          cleanedCount++;
        } catch (error) {
          console.warn('Failed to emergency clean lock:', lockDoc.id, error);
        }
      }
      
      ErrorTrackingService.addBreadcrumb(
        'Emergency lock cleanup completed',
        'lock_manager',
        'warning',
        { businessId, cleanedCount, reason }
      );
      
      return {
        success: true,
        cleanedCount,
        totalFound: locksSnapshot.docs.length,
        reason
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingLockManager',
        method: 'emergencyCleanup',
        businessId,
        reason
      });
      
      return {
        success: false,
        error: error.message,
        cleanedCount: 0
      };
    }
  }
}